import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Sparkles, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CouncilPrivacyNote } from "./CouncilMessages";

export default function ContactCouncil() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kind, setKind] = useState("inquiry");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const send = async () => {
    if (!message.trim()) return;
    setBusy(true);
    try { await api.post("/council-messages", { kind, message: message.trim(), sender_name: user?.name }); setDone(true); }
    catch { alert("Could not send. Please try again."); }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 md:px-8 py-16 text-center" data-testid="council-sent">
        <ShieldCheck className="mx-auto text-[#14A89A] mb-4" size={34} />
        <h1 className="font-heading text-2xl text-white mb-3">Received by the Council</h1>
        <p className="font-body text-sm text-zinc-400 mb-8">Thank you. The Council will review this. If we reply, you'll get a notification and can see it in My messages to the Council.</p>
        <button onClick={() => navigate("/council/messages")} data-testid="council-view-messages" className="block mx-auto mb-3 text-[#14A89A] hover:underline font-body text-sm">My messages to the Council</button>
        <button onClick={() => navigate("/feed")} data-testid="council-done-button" className="border border-white/15 text-zinc-300 hover:border-[#14A89A] hover:text-white px-6 py-3 font-body text-sm transition-colors">Back to The Signal</button>
      </div>
    );
  }

  const Option = ({ value, icon: Icon, title, sub }) => (
    <button onClick={() => setKind(value)} data-testid={`council-kind-${value}`} className={`w-full text-left border p-4 flex items-start gap-3 transition-colors ${kind === value ? "border-[#14A89A] bg-[#14A89A]/[0.06]" : "border-white/15 hover:border-white/30"}`}>
      <Icon size={20} className={kind === value ? "text-[#14A89A] mt-0.5" : "text-zinc-500 mt-0.5"} />
      <div>
        <div className="font-body text-sm text-white">{title}</div>
        <div className="font-body text-xs text-zinc-500 mt-0.5">{sub}</div>
      </div>
    </button>
  );

  return (
    <div className="max-w-xl mx-auto px-4 md:px-8 py-8" data-testid="contact-council-page">
      <div className="flex items-center justify-between mb-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs font-body"><ArrowLeft size={14} /> Back</button>
        <button onClick={() => navigate("/council/messages")} data-testid="council-my-messages-link" className="font-body text-xs text-[#14A89A] hover:underline">My messages to the Council →</button>
      </div>
      <h1 className="font-heading text-3xl text-white mb-2">Contact the Council</h1>
      <p className="font-body text-sm text-zinc-400 mb-8">Reach the Council directly. Your name is included so a council member can reply to you.</p>

      <div className="space-y-3 mb-6">
        <Option value="disturbance" icon={AlertTriangle} title="Disturbance in the Field" sub="Report something that feels off in the community." />
        <Option value="inquiry" icon={Sparkles} title="Question, concern, idea or technical" sub="Reach the Council about anything else." />
      </div>

      <label className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">Please explain</label>
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} placeholder="Share the details with the Council..." data-testid="council-message-input" className="mt-3 w-full bg-[#0A0A0A] border border-white/15 px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body resize-none text-sm" />

      <button onClick={send} disabled={busy || !message.trim()} data-testid="council-send-button" className="mt-6 w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3.5 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />} Send to the Council
      </button>
      <div className="mt-5"><CouncilPrivacyNote /></div>
    </div>
  );
}
